/**
 * Durable user-switch intent.
 *
 * A user-switch mints a short-lived, access-only OIDC token (no refresh token)
 * that lives only in the in-memory store — so a browser reload drops it and the
 * app falls back to the original admin token. To keep the switch across reloads
 * we persist the switch *target* here and re-exchange it during app init (see
 * `restoreUserSwitch` in layoutHandler): every load re-derives a fresh switch
 * token from the admin session, which also survives the switch token's expiry.
 */
const KEY = 'cc_switch_target';

export interface UserSwitchTarget {
  user: string;
  customer: string;
}

export function saveUserSwitchTarget(target: UserSwitchTarget): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(target));
  } catch {
    // storage unavailable → switch simply won't survive reload
  }
}

export function clearUserSwitchTarget(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

export function readUserSwitchTarget(): UserSwitchTarget | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<UserSwitchTarget>;
    return typeof parsed?.user === 'string' && typeof parsed?.customer === 'string'
      ? {user: parsed.user, customer: parsed.customer}
      : null;
  } catch {
    return null;
  }
}
